import { createContext, useContext, useState, useCallback } from 'react';
import { toast } from 'sonner';

const CompareContext = createContext();

const MAX_COMPARE_ITEMS = 4;

export function CompareProvider({ children }) {
  const [compareItems, setCompareItems] = useState([]);
  const [showCompareModal, setShowCompareModal] = useState(false);

  const addToCompare = useCallback((item) => {
    setCompareItems(prev => {
      // Check if already in compare list
      if (prev.some(i => i.id === item.id)) {
        toast.info('الرقم موجود بالفعل في قائمة المقارنة');
        return prev;
      }
      
      // Check max limit
      if (prev.length >= MAX_COMPARE_ITEMS) {
        toast.warning(`يمكنك مقارنة ${MAX_COMPARE_ITEMS} أرقام كحد أقصى`);
        return prev;
      }
      
      toast.success('تمت الإضافة للمقارنة');
      return [...prev, item];
    });
  }, []);

  const removeFromCompare = useCallback((itemId) => {
    setCompareItems(prev => prev.filter(item => item.id !== itemId));
  }, []);

  const clearCompare = useCallback(() => {
    setCompareItems([]);
    setShowCompareModal(false);
  }, []);

  const isInCompare = useCallback((itemId) => {
    return compareItems.some(item => item.id === itemId);
  }, [compareItems]);

  const toggleCompare = useCallback((item) => {
    if (isInCompare(item.id)) {
      removeFromCompare(item.id);
      toast.info('تمت الإزالة من المقارنة');
    } else {
      addToCompare(item);
    }
  }, [isInCompare, addToCompare, removeFromCompare]);

  const openCompareModal = useCallback(() => {
    if (compareItems.length < 2) {
      toast.warning('اختر رقمين على الأقل للمقارنة');
      return;
    }
    setShowCompareModal(true);
  }, [compareItems.length]);

  const closeCompareModal = useCallback(() => {
    setShowCompareModal(false);
  }, []);

  const value = {
    compareItems,
    compareCount: compareItems.length,
    maxItems: MAX_COMPARE_ITEMS,
    addToCompare,
    removeFromCompare,
    clearCompare,
    isInCompare,
    toggleCompare,
    showCompareModal,
    openCompareModal,
    closeCompareModal
  };

  return (
    <CompareContext.Provider value={value}>
      {children}
    </CompareContext.Provider>
  );
}

export function useCompare() {
  const context = useContext(CompareContext);
  if (!context) {
    throw new Error('useCompare must be used within a CompareProvider');
  }
  return context;
}
